import { Link } from "react-router-dom";
import { ArrowRight, Cog, Gauge, ShieldCheck, Ship, Zap } from "lucide-react";
import { COMPANY } from "@/lib/api";

const STATS = [
  { v: "45+", k: "YEARS / IN TRADE" },
  { v: "100s", k: "PARTS / IN STOCK" },
  { v: "UK-WIDE", k: "NEXT-DAY DELIVERY" },
  { v: "HGV·PSV", k: "INDUSTRIAL · MARINE" },
];

const SERVICES = [
  {
    icon: Cog,
    title: "Starter Motors",
    body: "Heavy-duty starter motors for HGV, PSV, industrial machinery and marine engines. 12V / 24V, tested and ready to fit.",
  },
  {
    icon: Zap,
    title: "Alternators & Charging Systems",
    body: "Output-graded alternators built for the demanding electrical loads of commercial, industrial and marine applications.",
  },
  {
    icon: Ship,
    title: "Trade & Fleet Supply",
    body: "Workshop accounts and next-day couriers for HGV operators, PSV depots, industrial sites and marine yards.",
  },
];

const STEPS = [
  ["01", "Identify", "VRN, model or part number. We confirm fitment first time."],
  ["02", "Quote", "Fixed price, no surprises — +VAT."],
  ["03", "Build / Ship", "Pulled from stock or built within 24-48h."],
];

const LOGOS = [
  "HGV", "PSV", "INDUSTRIAL", "MARINE",
  "DAF", "VOLVO", "SCANIA", "MAN",
  "IVECO", "MERCEDES-BENZ", "PERKINS", "CUMMINS",
];

export default function Home() {
  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-ink-600 noise">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1714352069805-e0bf94d1381f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800"
            alt=""
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-ink-900 via-ink-900/80 to-transparent" />
          <div className="absolute inset-0 grid-lines opacity-50" />
        </div>

        <div className="relative max-w-[1400px] mx-auto grid grid-cols-12 min-h-[80vh] items-stretch">
          <div className="col-span-12 md:col-span-8 px-5 md:px-12 py-16 md:py-28 flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-8">
              <span className="w-12 h-px bg-copper" />
              <span className="font-mono-tech text-[11px] text-copper">NORTH EAST ENGLAND</span>
            </div>

            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.9] mb-8 max-w-4xl">
              Automotive parts.<br />
              <span className="chrome-text">Built to last.</span><br />
              Shipped fast.
            </h1>

            <p className="text-steel-200 text-lg max-w-xl mb-10 leading-relaxed">
              Washington Rotating Electrics supplies starter motors and alternators for{" "}
              <span className="text-copper">HGV</span>,{" "}
              <span className="text-copper">PSV</span>,{" "}
              <span className="text-copper">industrial</span> and{" "}
              <span className="text-copper">marine</span> applications across the UK.
              Tested. Trusted. On the shelf and out the door.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link to="/quote" data-testid="hero-cta-quote" className="btn-copper">
                Get a Free Quote <ArrowRight size={14} />
              </Link>
              <Link to="/catalog" data-testid="hero-cta-catalog" className="btn-ghost">
                Browse Catalog
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 mt-16 border-t border-ink-600">
              {STATS.map((s) => (
                <div key={s.k} className="py-6 border-r last:border-r-0 border-ink-600 pr-4">
                  <div className="font-display text-3xl md:text-4xl text-copper">{s.v}</div>
                  <div className="font-mono-tech text-[9px] text-steel-400 mt-1">{s.k}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden md:block col-span-4 border-l border-ink-600 relative">
            <div className="absolute top-0 left-0 right-0 bottom-0">
              <div className="absolute top-8 left-8 right-8 bottom-10 font-mono-tech text-[10px] text-steel-400 leading-relaxed flex flex-col justify-between">
                <div>
                  <div className="text-copper mb-3">WHAT WE SUPPLY</div>
                  <div className="space-y-2 normal-case tracking-normal text-sm text-steel-200 font-sans">
                    <div>⏵ Starter motors</div>
                    <div>⏵ Alternators</div>
                    <div>⏵ Rebuild kits & spares</div>
                    <div>⏵ HGV · PSV · Industrial · Marine</div>
                  </div>
                </div>
                <div className="text-steel-500">
                  ⏵ 12V / 24V SYSTEMS<br />
                  ⏵ HEAVY-DUTY APPLICATIONS<br />
                  ⏵ NEXT-DAY UK SHIPPING<br />
                  ⏵ TRADE DISCOUNTS · CORE BUY-BACK
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* marquee */}
        <div className="border-t border-ink-600 bg-ink-800 overflow-hidden ticker-mask">
          <div className="flex gap-10 animate-marquee whitespace-nowrap py-4">
            {[...LOGOS, ...LOGOS].map((l, i) => (
              <span key={i} className="font-mono-tech text-xs text-steel-500">
                {l} ·
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="border-b border-ink-600">
        <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-0">
          <div className="col-span-12 md:col-span-4 p-8 md:p-12 border-r border-ink-600">
            <div className="font-mono-tech text-[10px] text-copper mb-4">/ 01 — WHAT WE DO</div>
            <h2 className="font-display text-4xl md:text-5xl leading-[0.95] mb-6">
              Built for the workshop floor.
            </h2>
            <p className="text-steel-300 text-base leading-relaxed">
              We don&apos;t sell generic boxed parts. Every part we supply is
              hand-rebuilt, tested under load and signed off by a technician.
            </p>
          </div>

          <div className="col-span-12 md:col-span-8 grid grid-cols-1 sm:grid-cols-3">
            {SERVICES.map((s, i) => (
              <div
                key={s.title}
                data-testid={`service-card-${i}`}
                className={`p-8 border-ink-600 border-b sm:border-b-0 ${i < 2 ? "sm:border-r" : ""} hover:bg-ink-800 transition-colors`}
              >
                <s.icon className="text-copper mb-5" size={28} strokeWidth={1.6} />
                <h3 className="font-display text-2xl mb-3">{s.title}</h3>
                <p className="text-steel-400 text-sm leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="border-b border-ink-600 bg-ink-800">
        <div className="max-w-[1400px] mx-auto px-5 md:px-12 py-16 md:py-20">
          <div className="font-mono-tech text-[10px] text-copper mb-4">/ 03 — PROCESS</div>
          <h2 className="font-display text-4xl md:text-5xl leading-[0.95] mb-12 max-w-3xl">
            Three steps from your part number to a unit on your bench.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-ink-600">
            {STEPS.map(([n, t, b], i) => (
              <div
                key={n}
                className={`p-8 border-ink-600 ${i < 2 ? "md:border-r" : ""} border-b md:border-b-0 hover:bg-ink-900 transition-colors`}
              >
                <div className="font-display text-5xl text-copper mb-4">{n}</div>
                <div className="font-mono-tech text-[10px] text-steel-400 mb-2">STEP / {n}</div>
                <h3 className="font-display text-xl mb-3">{t}</h3>
                <p className="text-steel-400 text-sm leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST BAND */}
      <section className="border-b border-ink-600">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-3">
          {[
            { icon: ShieldCheck, t: "All Makes & Models", b: "Whatever the vehicle, we stock or source the right part — first time." },
            { icon: Gauge, t: "Dyno-Tested", b: "100% load-tested before dispatch. No exceptions." },
            { icon: Ship, t: "Next-Day UK", b: "Order before 15:30, on a courier the same evening." },
          ].map((x, i) => (
            <div key={x.t} className={`p-8 md:p-10 flex gap-5 items-start border-ink-600 ${i < 2 ? "md:border-r" : ""} border-b md:border-b-0`}>
              <x.icon className="text-copper shrink-0" size={32} strokeWidth={1.6} />
              <div>
                <h3 className="font-display text-xl mb-2">{x.t}</h3>
                <p className="text-steel-400 text-sm">{x.b}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-ink-600 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1676018366904-c083ed678e60?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800"
            alt=""
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-ink-900/70" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-12 py-20 md:py-28 grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 md:col-span-8">
            <div className="font-mono-tech text-[10px] text-copper mb-4">/ NEED A UNIT TODAY?</div>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.95] mb-6">
              Send us your VRN. We&apos;ll do the rest.
            </h2>
            <p className="text-steel-300 max-w-xl">
              Trade or retail. Send the vehicle reg or old part number — we&apos;ll come back
              with a fixed price within one working hour during opening times.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 flex flex-col gap-4">
            <Link to="/quote" className="btn-copper justify-between !px-6 !py-4">
              Request Quote <ArrowRight size={16} />
            </Link>
            <a href={`tel:${COMPANY.phone.replace(/\s+/g, "")}`} className="btn-ghost justify-between !px-6 !py-4">
              Call {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
